/**
 * Public charge — the standing answer, as content, not as a rules engine.
 *
 * Philanthropic cash is not a public benefit under any version of the public
 * charge test, so there is nothing here to evaluate per member and nothing to
 * store. This is a versioned explainer a counsellor can read aloud or hand
 * over, and the version is printed so it is obvious which wording was current
 * when somebody relied on it.
 *
 * WHY THERE IS NO FIELD ON THIS SCREEN
 * No immigration status is recorded anywhere in this feature — no column, no
 * form field, no screener item. There is then nothing to leak, export or
 * subpoena, and enrolment does not feel like creating a government-visible
 * record. (The schema does carry a dormant `member_immigration_records` table
 * with encrypted columns and zero code reading or writing it. Nothing in this
 * feature may start using it; that it exists is not permission.)
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

/**
 * Bump on any wording change, and say what changed. A counsellor who told
 * somebody something in November needs to be able to find what the words were
 * in November.
 */
export const PUBLIC_CHARGE_CONTENT_VERSION = '2026.10.1';
export const PUBLIC_CHARGE_RULE_EFFECTIVE = '18 September 2026';

export const PublicChargeExplainer: React.FC<{ defaultOpen?: boolean }> = ({
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card>
      <CardContent className="py-4">
        <button
          type="button"
          className="flex w-full items-start justify-between gap-3 text-left"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="flex items-start gap-2">
            <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            <span>
              <span className="block text-sm font-medium text-foreground">
                Public charge: this payment is not a public benefit
              </span>
              <span className="block text-sm text-muted-foreground">
                What to say if a participant asks whether taking this money affects her
                immigration case.
              </span>
            </span>
          </span>
          {open ? (
            <ChevronUp className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronDown className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
        </button>

        {open && (
          <div className="mt-4 space-y-3 border-t border-border pt-4 text-sm text-foreground">
            <p>
              This payment is <strong>private charitable assistance from a foundation</strong>. It
              is not a federal, state or local public benefit, and it is not cash assistance for
              income maintenance. Nothing about receiving it is a public benefit for public charge
              purposes.
            </p>

            <p>
              Under the totality-of-circumstances test, resources and income count in a
              participant's <strong>favour</strong>. Income from a private source is the kind of
              fact that helps rather than harms.
            </p>

            <div className="rounded-md border border-border bg-muted/50 p-3">
              <p className="font-medium text-foreground">Two things to be careful about</p>
              <ul className="mt-2 list-inside list-disc space-y-1.5 text-muted-foreground">
                <li>
                  The guidance current from {PUBLIC_CHARGE_RULE_EFFECTIVE} is{' '}
                  <strong>not retroactive</strong>. It does not change how anything before that
                  date was assessed, and it is not a basis for revisiting a past determination.
                </li>
                <li>
                  Advice that protects a participant's benefits can damage her immigration case,
                  and the reverse. Where immigration status is in play, that question goes to
                  immigration counsel <strong>before</strong> any benefits advice — not after, and
                  not alongside.
                </li>
              </ul>
            </div>

            <p className="text-muted-foreground">
              This is general information about the programme, not legal advice, and no one here
              may give immigration advice. We do not ask about, record or hold anyone's
              immigration status.
            </p>

            <p className="text-xs text-muted-foreground">
              Content version {PUBLIC_CHARGE_CONTENT_VERSION} · reflects guidance effective{' '}
              {PUBLIC_CHARGE_RULE_EFFECTIVE}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PublicChargeExplainer;
